import 'froala-editor/js/plugins/align.min.js';
import 'froala-editor/js/plugins/colors.min.js';
import 'froala-editor/js/plugins/font_size.min.js';
import 'froala-editor/js/plugins/lists.min.js';
import 'froala-editor/js/plugins/image.min.js';
import 'froala-editor/js/plugins/font_family.min.js';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import FroalaEditor, { AlignType, FroalaEvents, FroalaOptions } from 'froala-editor';
import { of } from 'rxjs';
import { FireflyFroalaLicenseProvider, FireflyLocalizationService, isMac, isSafari } from '../utils';
import { FireflyHashMap } from '../utils/localization/firefly-localization.model';
import { alignmentIcons, buttonsToTriggerClearFormatting, fonts, icons } from './models/constants';
import { TextEditorValidation } from './models/text-editor-validation.model';
import { ValidationError } from './models/text-editor-validation-error.enum';

@Component({
  selector: 'f-text-editor',
  templateUrl: './text-editor.component.html',
  imports: [FroalaEditorModule, FroalaViewModule, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FireflyTextEditorComponent implements OnInit {
  @ViewChild('contextualMenu') contextualMenu!: ElementRef;

  validation = input<TextEditorValidation>();
  additionalButtons = input<string[][]>([]);
  editor = input<FormControl<string | null>>(new FormControl(''));
  placeholder = input<string>('');
  additionalEvents = input<Partial<FroalaEvents>>({});

  editorOptions!: Partial<FroalaOptions>;
  readonly localization = toSignal(
    inject(FireflyLocalizationService, { optional: true })?.getLocalization() ?? of({} as FireflyHashMap),
    { initialValue: {} as FireflyHashMap }
  );
  readonly requiredErrorMessage = computed(() => this.localization()['fieldIsRequired'] || 'This field is required');
  readonly urlPopupWebAddressMessage = computed(
    () => this.localization()['urlPopupWebAddressMessage'] || 'Web Address'
  );
  readonly urlPopupTextToDisplayMessage = computed(
    () => this.localization()['urlPopupTextToDisplayMessage'] || 'Text to Display'
  );
  readonly urlPopupWebAddressPlaceholderMessage = computed(
    () => this.localization()['urlPopupWebAddressPlaceholderMessage'] || 'Enter web address'
  );
  readonly urlPopupTextToDisplayPlaceholderMessage = computed(
    () => this.localization()['urlPopupTextToDisplayPlaceholderMessage'] || 'Enter text to display'
  );
  readonly colorPickerButtonMessage = computed(() => this.localization()['colorPickerButtonMessage'] || 'Apply');
  readonly colorPickerPlaceholderMessage = computed(
    () => this.localization()['colorPickerPlaceholderMessage'] || 'Enter HEX color'
  );
  readonly imageInsertTitleMessage = computed(
    () => this.localization()['imageInsertTitle'] || 'Drag and drop to upload file or'
  );
  readonly imageUploaderBrowseFilesMessage = computed(
    () => this.localization()['uploaderBrowseFiles'] || 'Browse Files'
  );
  readonly alignMessage = computed(() => this.localization()['align'] || 'Align');
  readonly alignLeftMessage = computed(() => this.localization()['alignLeft'] || 'Align Left');
  readonly alignCenterMessage = computed(() => this.localization()['alignCenter'] || 'Align Center');
  readonly alignRightMessage = computed(() => this.localization()['alignRight'] || 'Align Right');
  readonly alignJustifyMessage = computed(() => this.localization()['alignJustify'] || 'Align Justify');

  private renderer = inject(Renderer2);
  private licenseService = inject(FireflyFroalaLicenseProvider, { optional: true });

  ngOnInit(): void {
    this.defineIcons(icons);
    this.addAlignmentButtons(this);

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const component = this;

    // Get Froala's default list of allowed attributes
    const defaultAllowedAttrs = FroalaEditor.DEFAULTS.htmlAllowedAttrs;
    // Add custom attributes to the default list
    const extendedAllowedAttrs = [...defaultAllowedAttrs, 'ses:no-track', 'ses:tags'];

    this.editorOptions = {
      key: this.license,
      toolbarSticky: false,
      toolbarButtons: [
        ['undo', 'redo'],
        ['bold', 'italic', 'underline'],
        ['backgroundColor'],
        ['fontFamily'],
        ['fontSize'],
        ['textColor'],
        ['alignLeft', 'alignCenter', 'alignRight', 'alignJustify'],
        ['formatOLSimple', 'formatUL'],
        ...(this.additionalButtons() || []),
        ['clearFormatting']
      ],
      tableEditButtons: [
        'tableHeader',
        'tableFooter',
        'tableRows',
        'tableColumns',
        'tableStyle',
        'tableCells',
        'tableCellBackground',
        '-',
        'tableCellVerticalAlign',
        'tableAlignLeft',
        'tableAlignCenter',
        'tableAlignRight',
        'tableAlignJustify',
        'tableRemove'
      ],
      imageEditButtons: [
        'imageReplace',
        'imageAlignLeft',
        'imageAlignCenter',
        'imageAlignRight',
        '-',
        'imageCaption',
        'imageDisplay',
        'imageRemove'
      ],
      events: {
        initialized: function () {
          const toolbar = this.$tb[0] as HTMLElement;
          const fontSizeBtn = toolbar.querySelectorAll('button[data-cmd="fontSize"]');
          const fontSizeBtnSpan = toolbar.querySelectorAll('button[data-cmd="fontSize"] span');
          const buttons = toolbar.querySelectorAll('button.fr-command');
          const selection = this.selection.get() as Selection;
          const clearFormattingButton = toolbar.querySelector('[data-cmd="clearFormatting"]');

          fontSizeBtn.forEach(item => item.setAttribute('title', 'Size'));
          fontSizeBtnSpan.forEach(item => (item.textContent = 'Size'));
          component.replaceSVGWithIcons(alignmentIcons);

          Array.from(buttons)
            .filter(button => buttonsToTriggerClearFormatting.includes(button.getAttribute('data-cmd')!))
            .forEach(item =>
              component.renderer.listen(item, 'click', () => component.toggleClearFormattingButton(toolbar, selection))
            );

          component.renderer.setAttribute(clearFormattingButton, 'disabled', 'disabled');

          this.popups.onShow = () => {
            this.button.bulkRefresh = function () {
              // bypass Froala's logic to prevent losing the active states of toolbar buttons.
            };
          };
        },
        'popups.hide.backgroundColor.picker': function () {
          const selection = this.selection.get() as Selection;
          const toolbar = this.$tb[0] as HTMLElement;
          component.toggleClearFormattingButton(toolbar, selection);
        },
        'popups.hide.textColor.picker': function () {
          const selection = this.selection.get() as Selection;
          const toolbar = this.$tb[0] as HTMLElement;
          component.toggleClearFormattingButton(toolbar, selection);
        },
        'popups.show.table.edit': function () {
          const popup = this.popups.get('table.edit')[0];
          component.replaceSVGWithIcons(alignmentIcons, popup);
        },
        'popups.show.table.colors': function () {
          const popup = this.popups.get('table.colors')[0];
          component.setupColorPickerHandler(popup);
        },
        'popups.show.backgroundColor.picker': function () {
          const popup = this.popups.get('backgroundColor.picker')[0];
          component.setupColorPickerHandler(popup);
        },
        'popups.show.textColor.picker': function () {
          const popup = this.popups.get('textColor.picker')[0];
          component.setupColorPickerHandler(popup);
        },
        'popups.show.image.insert': function () {
          const popup = this.popups.get('image.insert')[0];
          component.setupImageUploader(popup);
        },
        'popups.show.image.edit': function () {
          const popup = this.popups.get('image.edit')[0];
          component.replaceSVGWithIcons(alignmentIcons, popup);
        },
        'popups.show.link.insert': function () {
          const popup = this.popups.get('link.insert')[0];

          const updateLabelWithStar = (selector: string, message: string) => {
            const labelElement = popup.querySelector(selector);
            const starElement = component.renderer.createElement('span') as HTMLElement;
            labelElement.textContent = message;
            component.renderer.addClass(starElement, 'text-danger');
            starElement.textContent = '*';
            component.renderer.appendChild(labelElement, starElement);

            return labelElement;
          };

          updateLabelWithStar('.fr-link-attr[name="href"] + label', component.urlPopupWebAddressMessage());
          updateLabelWithStar('.fr-link-attr[name="text"] + label', component.urlPopupTextToDisplayMessage());

          const updateInputPlaceholder = (selector: string, placeholderMessage: string) =>
            component.renderer.setAttribute(popup.querySelector(selector), 'placeholder', placeholderMessage);
          updateInputPlaceholder('.fr-link-attr[name="href"]', component.urlPopupWebAddressPlaceholderMessage());
          updateInputPlaceholder('.fr-link-attr[name="text"]', component.urlPopupTextToDisplayPlaceholderMessage());
        },
        blur: function () {
          component.editor().markAsTouched();

          if (!component.contextualMenu.nativeElement.children.length) return;
          const contextualMenu = component.contextualMenu?.nativeElement;
          const editor = this as unknown as FroalaEditor;
          if (contextualMenu.classList.contains('show')) component.hideContextualMenu(editor, contextualMenu);
        },
        focus: () => {
          setTimeout(() => this.editor().markAsUntouched(), 0);
        },
        mouseup: function (e: MouseEvent) {
          if (!component.contextualMenu.nativeElement.children.length) return;
          const contextualMenuElement = component.contextualMenu.nativeElement;
          const editor = this as unknown as FroalaEditor;
          const range = editor.selection.ranges(0) as Range;
          const newRange = document.createRange();
          const parentRect = editor.el.parentElement.parentElement.getBoundingClientRect();

          newRange.setStart(range.startContainer, range.startOffset);
          newRange.setEnd(range.endContainer, range.endOffset);

          const x = newRange.getBoundingClientRect().left - parentRect.left;
          const y = newRange.getBoundingClientRect().top - parentRect.top;

          // editor.el.oncontextmenu = () => false; doesn't work in Safari and MacOS, so we need to disable the browser context menu
          if (isSafari() || isMac()) editor.el.addEventListener('contextmenu', (e: MouseEvent) => e.preventDefault());

          if (component.isRightMouseButton(e) && editor.selection.text().length) {
            component.showContextualMenu(x, y, editor, contextualMenuElement);
          }

          const links = editor.el.querySelectorAll('a');
          links.forEach((link: HTMLElement) => {
            if (component.isRightMouseButton(e) && (e.target as HTMLElement).tagName === 'A') {
              const linkRect = link.getBoundingClientRect();
              const x = linkRect.left - parentRect.left;
              const y = linkRect.top - parentRect.top;
              component.showContextualMenu(x, y, editor, contextualMenuElement);
            }
          });

          requestAnimationFrame(() => {
            if (
              editor.selection.isCollapsed() &&
              (e.target! as HTMLElement).tagName !== 'A' &&
              contextualMenuElement.classList.contains('show')
            )
              component.hideContextualMenu(editor, contextualMenuElement);
          });
        },
        click: function () {
          const selection = this.selection.get() as Selection;
          const toolbar = this.$tb[0] as HTMLElement;
          component.toggleClearFormattingButton(toolbar, selection);
        },
        'image.beforeUpload': function (files: File[]) {
          if (files.length) {
            const reader = new FileReader();

            reader.onload = (event: ProgressEvent<FileReader>) => {
              const result = event.target?.result as string;
              this.image.insert(result, false, {}, this.image.get());
            };

            reader.readAsDataURL(files[0]);
          }

          this.popups.hideAll();
          return false; // Stop default upload
        },
        ...this.additionalEvents()
      },
      placeholderText: this.placeholder() || '',
      fontSize: ['8', '10', '12', '14', '18', '24', '36'],
      fontFamilyDefaultSelection: 'Arial',
      fontFamily: Object.fromEntries(fonts.sort((a, b) => a[1].localeCompare(b[1]))),
      fontFamilySelection: true,
      imageOutputSize: true,
      fontSizeSelection: true,
      fontSizeUnit: 'pt',
      paragraphFormatSelection: true,
      attribution: false,
      linkEditButtons: [],
      htmlAllowedAttrs: extendedAllowedAttrs,
      scrollableContainer: '.fr-box',
      useClasses: false
    };
  }

  get requiredAndInvalid(): boolean | undefined {
    return !(this.validation()?.error === ValidationError.Required)
      ? undefined
      : this.editor().errors?.required && this.editor().touched;
  }

  get isCustomError(): boolean {
    return this.validation()?.error === ValidationError.Custom && this.editor().touched;
  }

  private get license(): string {
    return this.licenseService?.getLicense() || '';
  }

  private isRightMouseButton(e: MouseEvent): boolean {
    return e.button === 2;
  }

  private showContextualMenu(x: number, y: number, editor: FroalaEditor, contextualMenu: HTMLElement): void {
    const lineHeight = 16;
    editor.el.oncontextmenu = () => false;
    this.renderer.setStyle(contextualMenu, 'transform', `translate(${x}px, ${y + lineHeight}px)`);
    this.renderer.addClass(contextualMenu, 'show');
  }

  private hideContextualMenu(editor: FroalaEditor, contextualMenu: HTMLElement): void {
    editor.el.oncontextmenu = null;
    this.renderer.removeClass(contextualMenu, 'show');
  }

  private setupColorPickerHandler(popup: HTMLElement): void {
    const colorPickerClearIcon = popup.querySelector('.fr-submit');
    const input = popup.querySelector('input');
    this.renderer.setProperty(colorPickerClearIcon, 'textContent', this.colorPickerButtonMessage());
    this.renderer.setAttribute(input, 'placeholder', this.colorPickerPlaceholderMessage());

    if (popup.querySelector(`.f-i-trash-bin`) === null) {
      const svgElement = popup.querySelector(`.fr-color-set .fr-svg`)!;
      const parentElement = svgElement.parentElement;
      const newIcon = this.renderer.createElement('i');
      this.renderer.addClass(newIcon, 'f-i');
      this.renderer.addClass(newIcon, 'f-i-trash-bin');
      this.renderer.appendChild(parentElement, newIcon);
    }
  }

  private addAlignmentButtons(component: this): void {
    const config: { type: string; icon: string; align: AlignType }[] = [
      { type: 'imageAlignLeft', icon: 'f-i-alignment', align: 'left' },
      { type: 'imageAlignRight', icon: 'f-i-right-alignment', align: 'right' },
      { type: 'imageAlignCenter', icon: 'f-i-center-alignment', align: 'center' },
      { type: 'tableAlignLeft', icon: 'f-i-alignment', align: 'left' },
      { type: 'tableAlignRight', icon: 'f-i-right-alignment', align: 'right' },
      { type: 'tableAlignCenter', icon: 'f-i-center-alignment', align: 'center' },
      { type: 'tableAlignJustify', icon: 'f-i-width-alignment', align: 'justify' }
    ];
    config.forEach(({ type, icon, align }) =>
      FroalaEditor.RegisterCommand(type, {
        title: this.alignMessage(),
        icon: `<i class="f-i f-i-xs ${icon}"></i>`,
        undo: true,
        focus: true,
        refreshAfterCallback: true,
        callback: function (button) {
          setTimeout(() => {
            // timeout to override froala active state style logic
            const el = document.querySelector(`button[data-cmd="${button}"]`);
            component.renderer.addClass(el, 'fr-active');
          }, 100);

          type.includes('image') ? this.image.align(align) : this.table.horizontalAlign(align);
        }
      })
    );
  }

  private setupImageUploader(popup: HTMLElement): void {
    const layer = popup.querySelector('.fr-image-upload-layer')!;
    const form = layer.querySelector('.fr-form')!;
    const el = `
    <p class="body-50">${this.imageInsertTitleMessage()}</p>
    <span class="link link-primary body-50">${this.imageUploaderBrowseFilesMessage()}</span>
    `;
    this.renderer.setProperty(layer, 'textContent', '');

    layer.innerHTML = `${el}${form.outerHTML}`;
  }

  private replaceSVGWithIcons(mappings: { [key: string]: string[] }, el?: HTMLElement): void {
    Object.entries(mappings).forEach(([cmd, iconClassList]) => {
      const selector = `button[data-cmd="${cmd}"]`;
      const svgElement = (el || document).querySelector(`${selector} .fr-svg`);

      if (svgElement) {
        const parentElement = svgElement.parentElement;
        this.renderer.removeChild(parentElement, svgElement);
        const newIcon = this.renderer.createElement('i');
        iconClassList.forEach(cls => this.renderer.addClass(newIcon, cls));
        this.renderer.appendChild(parentElement, newIcon);
      }
    });
  }

  private isTextFormatted(selection: Selection | null): boolean {
    if (selection && selection.rangeCount > 0 && selection.toString().trim() !== '') {
      const range = selection.getRangeAt(0);
      const commonAncestor = range.commonAncestorContainer;

      const containsDesiredTagsOrStyles = (node: HTMLElement): boolean => {
        const desiredTags = ['STRONG', 'EM', 'U'];
        const hasDesiredTag = desiredTags.includes(node.tagName);
        const inlineColor = node.style?.color;
        const inlineBackgroundColor = node.style?.backgroundColor;
        const inlineFontSize = node.style?.fontSize;
        const hasInlineStyles = Boolean(inlineColor || inlineBackgroundColor || inlineFontSize);

        return hasDesiredTag || hasInlineStyles;
      };

      let currentElement = (
        commonAncestor.nodeType === 3 ? commonAncestor.parentElement : commonAncestor
      ) as HTMLElement;

      while (currentElement) {
        if (containsDesiredTagsOrStyles(currentElement)) return true;

        currentElement = currentElement.parentElement!;
      }

      const fragment = range.cloneContents();
      const childNodes = Array.from(fragment.childNodes);

      for (const node of childNodes) if (containsDesiredTagsOrStyles(node as HTMLElement)) return true;
    }

    return false;
  }

  private toggleClearFormattingButton(toolbar: HTMLElement, selection: Selection): void {
    const clearFormattingButton = toolbar.querySelector('[data-cmd="clearFormatting"]');
    this.isTextFormatted(selection)
      ? this.renderer.removeAttribute(clearFormattingButton, 'disabled')
      : this.renderer.setAttribute(clearFormattingButton, 'disabled', 'disabled');
  }

  private defineIcons(icons: { [key: string]: string }): void {
    FroalaEditor.DefineIconTemplate('common', '<i class="f-i f-i-xs f-i-[NAME]"></i>');

    Object.entries(icons).forEach(([key, value]) => {
      FroalaEditor.DefineIcon(key, { NAME: value, template: 'common' });
    });
  }
}
